import { createFileRoute, Link, Outlet, useRouterState } from "@tanstack/react-router";
import { BarChart3, CalendarDays, LayoutDashboard, Menu, PanelLeftClose, PanelLeftOpen, Users, X } from "lucide-react";
import { useEffect, useState } from "react";
import { SMM_ROUTE_PATHS } from "@/lib/smm-routes";
import { useSmmAccess } from "@/components/crm/SmmAccessGate";
import { WorkspacePageTitle } from "@/components/WorkspaceHeader";

export const Route = createFileRoute("/crm/smm")({
  component: SmmLayout,
});

type SmmNavKey = "operations" | "calendar" | "reports" | "members";

const NAV: {
  key: SmmNavKey;
  label: string;
  navLabel: string;
  description: string;
  to: string;
  search?: Record<string, string>;
  icon: typeof LayoutDashboard;
  manageOnly?: boolean;
}[] = [
  {
    key: "operations",
    label: "Operations",
    navLabel: "Ops",
    description: "Client delivery, quotas and overdue work this month.",
    to: SMM_ROUTE_PATHS.dashboard,
    search: { view: "operations" },
    icon: LayoutDashboard,
  },
  {
    key: "calendar",
    label: "Calendar",
    navLabel: "Calendar",
    description: "Scheduled stories, feed posts and reels.",
    to: SMM_ROUTE_PATHS.dashboard,
    search: { view: "calendar" },
    icon: CalendarDays,
  },
  {
    key: "reports",
    label: "Reports",
    navLabel: "Reports",
    description: "Workplace activity across SMM clients and CRM deals.",
    to: SMM_ROUTE_PATHS.reports,
    icon: BarChart3,
  },
  {
    key: "members",
    label: "Members",
    navLabel: "Members",
    description: "Who can access the SMM workspace and their role.",
    to: SMM_ROUTE_PATHS.members,
    icon: Users,
    manageOnly: true,
  },
];

function useActiveSmmKey(): SmmNavKey {
  const pathname = useRouterState({ select: (s) => s.location.pathname });
  const search = useRouterState({ select: (s) => s.location.search as Record<string, unknown> });
  const view = typeof search.view === "string" ? search.view : undefined;
  if (pathname.startsWith("/crm/smm/members")) return "members";
  if (pathname.startsWith("/crm/smm/reports")) return "reports";
  if (view === "members") return "members";
  if (view === "calendar") return "calendar";
  if (view === "reports") return "reports";
  return "operations";
}

function SmmLayout() {
  const { data: access } = useSmmAccess();
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [collapsed, setCollapsed] = useState(false);
  useEffect(() => {
    try {
      setCollapsed(localStorage.getItem("smm.sidebar.collapsed") === "1");
    } catch {
      /* ignore */
    }
  }, []);
  const toggleCollapsed = () => {
    setCollapsed((v) => {
      const next = !v;
      try {
        localStorage.setItem("smm.sidebar.collapsed", next ? "1" : "0");
      } catch {
        /* ignore */
      }
      return next;
    });
  };
  const canManage = access?.canManage === true;
  const activeKey = useActiveSmmKey();
  const items = NAV.filter((n) => !n.manageOnly || canManage);
  const active = NAV.find((n) => n.key === activeKey) ?? NAV[0];

  return (
    <div className="flex-1 min-h-0 flex relative" style={{ fontFamily: "'Poppins', system-ui, sans-serif" }}>
      {sidebarOpen ? (
        <button
          type="button"
          aria-label="Close navigation"
          className="fixed inset-x-0 top-[53px] bottom-0 z-40 bg-black/40 md:hidden"
          onClick={() => setSidebarOpen(false)}
        />
      ) : null}

      <aside
        className={`${sidebarOpen ? "translate-x-0" : "-translate-x-full"} md:translate-x-0 fixed md:static top-[53px] md:top-auto bottom-0 left-0 z-40 ${collapsed ? "w-[220px] md:w-[56px]" : "w-[220px]"} min-h-0 overflow-y-auto overscroll-contain [scrollbar-width:thin] border-r bg-[#f8f9fb] transition-all duration-150 ease-out shadow-xl md:shadow-none`}
        style={{ borderColor: "#eceff3" }}
      >
        <div className="mb-1 flex items-center justify-between px-3 pt-3 md:hidden">
          <span className="text-sm font-semibold text-[#212529]">Menu</span>
          <button type="button" onClick={() => setSidebarOpen(false)} aria-label="Close" className="rounded-lg p-1.5 hover:bg-[#eef1f5]">
            <X className="size-4" />
          </button>
        </div>
        <div className="hidden md:flex items-center justify-end px-2 pt-2">
          <button
            type="button"
            onClick={toggleCollapsed}
            aria-label={collapsed ? "Expand sidebar" : "Collapse sidebar"}
            title={collapsed ? "Expand sidebar" : "Collapse sidebar"}
            className="rounded-lg p-1.5 text-[#4b5563] hover:bg-[#eef1f5]"
          >
            {collapsed ? <PanelLeftOpen className="size-4" /> : <PanelLeftClose className="size-4" />}
          </button>
        </div>
        <div className="px-2 py-2 pb-6">
          <div className={`px-2 pb-1 pt-1 text-[10px] font-semibold uppercase tracking-[0.14em] text-[#8b95a1] ${collapsed ? "md:hidden" : ""}`}>
            Workspace
          </div>
          <nav className="space-y-px">
            {items.map((n) => {
              const Icon = n.icon;
              const on = n.key === activeKey;
              return (
                <Link
                  key={n.key}
                  to={n.to}
                  search={(n.search ?? {}) as never}
                  onClick={() => setSidebarOpen(false)}
                  title={n.label}
                  className={`w-full flex items-center gap-2 px-1.5 py-[5px] rounded-md text-[12px] font-medium ${collapsed ? "md:justify-center" : ""}`}
                  style={{
                    background: on ? "#0d6efd" : "transparent",
                    color: on ? "white" : "#1f2937",
                  }}
                  onMouseEnter={(e) => {
                    if (!on) e.currentTarget.style.background = "#eef1f5";
                  }}
                  onMouseLeave={(e) => {
                    if (!on) e.currentTarget.style.background = "transparent";
                  }}
                >
                  <span
                    className="size-5 rounded-md flex items-center justify-center shrink-0"
                    style={{
                      background: on ? "rgba(255,255,255,0.18)" : "#eceff3",
                      color: on ? "white" : "#4b5563",
                    }}
                  >
                    <Icon className="size-3.5" strokeWidth={1.85} />
                  </span>
                  <span className={`truncate ${collapsed ? "md:hidden" : ""}`}>{n.navLabel}</span>
                </Link>
              );
            })}
          </nav>
        </div>
      </aside>

      <div className="flex-1 min-w-0 min-h-0 flex flex-col">
        <div className="md:hidden flex items-center gap-2 px-3 py-2 border-b bg-white" style={{ borderColor: "#eceff3" }}>
          <button
            type="button"
            className="rounded-lg p-2 text-[#212529] hover:bg-[#f1f3f5]"
            onClick={() => setSidebarOpen(true)}
            aria-label="Open menu"
          >
            <Menu className="size-5" />
          </button>
          <span className="text-sm font-semibold text-[#212529]">{active.label}</span>
        </div>
        <main className="flex-1 min-h-0 overflow-y-auto p-3 sm:p-5 md:p-7">
          <div className="mx-auto w-full max-w-[1320px]">
            <WorkspacePageTitle workspace="SMM" title={active.label} description={active.description} />
            <Outlet />
          </div>
        </main>
      </div>
    </div>
  );
}
